/**
 * Generates quarter-final matches ensuring protected teams don't face each other
 * Protected teams are from matches: 2, 3, 4, 6, 9, 12, 16
 */

export const PROTECTED_MATCH_IDS = [2, 3, 4, 6, 9, 12, 16];

/**
 * Get the winner from a match
 */
const getMatchWinner = (match) => {
  if (!match || match.winner === null || match.winner === undefined) return null;
  if (match.winner === 1 && match.player1) return match.player1;
  if (match.winner === 2 && match.player2) return match.player2;
  return null;
};

/**
 * Check if a match ID is in the protected list
 */
const isProtectedMatch = (matchId) => {
  return PROTECTED_MATCH_IDS.includes(matchId);
};

/**
 * Generate Group of 8 matches from Round of 16 winners
 * Simple pairing: 1st vs 2nd, 3rd vs 4th, etc.
 */
export const generateGroupOf8 = (roundOf16Matches) => {
  // Create a map of matchId to winner for easy lookup
  const winnersMap = new Map();
  roundOf16Matches.forEach(match => {
    const winner = getMatchWinner(match);
    if (winner) {
      winnersMap.set(match.id, winner);
    }
  });

  if (winnersMap.size !== 16) {
    return [];
  }

  // Create 8 matches with specific pairing:
  // 1 vs 16, 2 vs 15, 3 vs 13, 4 vs 14, 5 vs 12, 6 vs 11, 7 vs 10, 8 vs 9
  const pairingMap = [
    [1, 16],   // Match 1 vs Match 16
    [2, 15],   // Match 2 vs Match 15
    [3, 13],   // Match 3 vs Match 13
    [4, 14],   // Match 4 vs Match 14
    [5, 12],   // Match 5 vs Match 12
    [6, 11],   // Match 6 vs Match 11
    [7, 10],   // Match 7 vs Match 10
    [8, 9]     // Match 8 vs Match 9
  ];
  
  const groupOf8 = [];
  
  for (let i = 0; i < pairingMap.length; i++) {
    const [matchId1, matchId2] = pairingMap[i];
    const player1 = winnersMap.get(matchId1);
    const player2 = winnersMap.get(matchId2);
    
    if (player1 && player2) {
      groupOf8.push({
        id: i + 1,
        player1: player1,
        player2: player2,
        winner: null,
        score: null,
        frameScores: [],
        sourceMatch1: matchId1,
        sourceMatch2: matchId2
      });
    }
  }

  return groupOf8;
};

/**
 * Generate potential Group of 8 matches
 */
export const generatePotentialGroupOf8 = (roundOf16Matches) => {
  const matches = [];
  
  // Create a map of matchId to match data for easy lookup
  const matchesMap = new Map();
  roundOf16Matches.forEach(match => {
    matchesMap.set(match.id, {
      matchId: match.id,
      winner: getMatchWinner(match),
      player1: match.player1,
      player2: match.player2,
      isComplete: match.winner !== null
    });
  });

  // Create 8 matches with specific pairing:
  // 1 vs 16, 2 vs 15, 3 vs 13, 4 vs 14, 5 vs 12, 6 vs 11, 7 vs 10, 8 vs 9
  const pairingMap = [
    [1, 16],   // Match 1 vs Match 16
    [2, 15],   // Match 2 vs Match 15
    [3, 13],   // Match 3 vs Match 13
    [4, 14],   // Match 4 vs Match 14
    [5, 12],   // Match 5 vs Match 12
    [6, 11],   // Match 6 vs Match 11
    [7, 10],   // Match 7 vs Match 10
    [8, 9]     // Match 8 vs Match 9
  ];
  
  for (let i = 0; i < pairingMap.length; i++) {
    const [matchId1, matchId2] = pairingMap[i];
    const match1 = matchesMap.get(matchId1);
    const match2 = matchesMap.get(matchId2);
    
    if (match1 && match2) {
      const player1 = match1.winner
        ? match1.winner
        : { name: `Winner of Match ${match1.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };

      const player2 = match2.winner
        ? match2.winner
        : { name: `Winner of Match ${match2.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };

      matches.push({
        id: i + 1,
        player1,
        player2,
        winner: null,
        score: null,
        frameScores: [],
        sourceMatch1: match1.matchId,
        sourceMatch2: match2.matchId,
        isPotential: !match1.isComplete || !match2.isComplete
      });
    }
  }

  return matches;
};

/**
 * Generate quarter-final matches from Group of 8 winners
 * Protected teams (from matches 2, 3, 4, 6, 9, 12, 16) won't play each other
 */
export const generateQuarterFinals = (groupOf8Matches) => {
  // Get all winners from group of 8
  const winners = groupOf8Matches
    .map(match => ({
      matchId: match.id,
      player: getMatchWinner(match),
      sourceMatch1: match.sourceMatch1,
      sourceMatch2: match.sourceMatch2,
      // Check if this winner came from a protected match
      isProtected: match.sourceMatch1 ? isProtectedMatch(match.sourceMatch1) || isProtectedMatch(match.sourceMatch2) : false
    }))
    .filter(item => item.player !== null);

  if (winners.length !== 8) {
    return []; // Not all matches have winners yet
  }

  // Separate protected and non-protected teams
  const protectedTeams = winners.filter(w => w.isProtected);
  const nonProtectedTeams = winners.filter(w => !w.isProtected);

  // Shuffle non-protected teams for random assignment
  const shuffledNonProtected = [...nonProtectedTeams].sort(() => Math.random() - 0.5);

  // Create quarter-final matches
  // Each protected team will face a non-protected team
  const quarterFinals = [];
  let nonProtectedIndex = 0;

  // First, pair each protected team with a non-protected team
  // This ensures protected teams don't face each other
  protectedTeams.forEach((protectedTeam, index) => {
    if (nonProtectedIndex < shuffledNonProtected.length) {
      const opponent = shuffledNonProtected[nonProtectedIndex];
      nonProtectedIndex++;

      quarterFinals.push({
        id: index + 1,
        player1: protectedTeam.player,
        player2: opponent.player,
        winner: null,
        score: null,
        sourceMatch1: protectedTeam.matchId,
        sourceMatch2: opponent.matchId,
        isProtectedMatch: true
      });
    }
  });

  // Pair remaining non-protected teams together
  while (nonProtectedIndex < shuffledNonProtected.length) {
    const team1 = shuffledNonProtected[nonProtectedIndex];
    const team2 = shuffledNonProtected[nonProtectedIndex + 1];
    
    if (team1 && team2) {
      quarterFinals.push({
        id: quarterFinals.length + 1,
        player1: team1.player,
        player2: team2.player,
        winner: null,
        score: null,
        sourceMatch1: team1.matchId,
        sourceMatch2: team2.matchId,
        isProtectedMatch: false
      });
      nonProtectedIndex += 2;
    } else if (team1) {
      // If there's an odd number, the last team gets a bye (shouldn't happen with 8 teams)
      break;
    } else {
      break;
    }
  }

  return quarterFinals;
};

/**
 * Generate quarter-final matches from Group of 8 winners
 * 8 players, 4 matches
 */
export const generateQuarterFinalsFromGroupOf8 = (groupOf8Matches) => {
  // Create a map of matchId to winner for easy lookup
  const winnersMap = new Map();
  groupOf8Matches.forEach(match => {
    const winner = getMatchWinner(match);
    if (winner) {
      winnersMap.set(match.id, winner);
    }
  });

  if (winnersMap.size !== 8) {
    return [];
  }

  // QF pairings: 1v7, 2v3, 4v6, 5v8
  const pairingMap = [
    [1, 7],
    [2, 3],
    [4, 6],
    [5, 8]
  ];

  const quarterFinals = [];
  for (let i = 0; i < pairingMap.length; i++) {
    const [matchId1, matchId2] = pairingMap[i];
    const player1 = winnersMap.get(matchId1);
    const player2 = winnersMap.get(matchId2);

    if (player1 && player2) {
      quarterFinals.push({
        id: i + 1,
        player1,
        player2,
        winner: null,
        score: null,
        frameScores: [],
        sourceMatch1: matchId1,
        sourceMatch2: matchId2
      });
    }
  }

  return quarterFinals;
};

/**
 * Generate potential quarter-final matches from Group of 8
 * 8 players, 4 matches (showing potential matchups)
 */
export const generatePotentialQuarterFinalsFromGroupOf8 = (groupOf8Matches) => {
  if (!groupOf8Matches || groupOf8Matches.length !== 8) return [];

  // Create a map of matchId to match for easy lookup
  const matchesMap = new Map();
  groupOf8Matches.forEach(match => {
    matchesMap.set(match.id, match);
  });

  // QF pairings: 1v7, 2v3, 4v6, 5v8
  const pairingMap = [
    [1, 7],
    [2, 3],
    [4, 6],
    [5, 8]
  ];

  const matches = [];
  for (let i = 0; i < pairingMap.length; i++) {
    const [matchId1, matchId2] = pairingMap[i];
    const match1 = matchesMap.get(matchId1);
    const match2 = matchesMap.get(matchId2);

    if (match1 && match2) {
      const player1 = getMatchWinner(match1)
        ? getMatchWinner(match1)
        : { name: `Winner of G8 ${match1.id}`, image: null, note: 'To be determined', isPlaceholder: true };

      const player2 = getMatchWinner(match2)
        ? getMatchWinner(match2)
        : { name: `Winner of G8 ${match2.id}`, image: null, note: 'To be determined', isPlaceholder: true };

      matches.push({
        id: i + 1,
        player1,
        player2,
        winner: null,
        score: null,
        frameScores: [],
        sourceMatch1: matchId1,
        sourceMatch2: matchId2,
        isPotential: !match1.winner || !match2.winner
      });
    }
  }

  return matches;
};

/**
 * Generate semi-final matches from quarter-final winners
 * 4 players, 2 matches
 */
export const generateSemiFinals = (quarterFinalMatches) => {
  // Create a map of matchId to winner for easy lookup
  const winnersMap = new Map();
  quarterFinalMatches.forEach(match => {
    const winner = getMatchWinner(match);
    if (winner) {
      winnersMap.set(match.id, winner);
    }
  });

  if (winnersMap.size !== 4) {
    return [];
  }

  // SF pairings: 1v3, 2v4
  const pairingMap = [
    [1, 3],
    [2, 4]
  ];

  return pairingMap.map(([ matchId1, matchId2 ], i) => ({
    id: i + 1,
    player1: winnersMap.get(matchId1),
    player2: winnersMap.get(matchId2),
    winner: null,
    score: null,
    frameScores: [],
    sourceMatch1: matchId1,
    sourceMatch2: matchId2
  }));
};

/**
 * Generate final match from semi-final winners
 * 2 players, 1 match
 */
export const generateFinal = (semiFinalMatches) => {
  const winners = semiFinalMatches
    .map(match => ({
      matchId: match.id,
      player: getMatchWinner(match)
    }))
    .filter(item => item.player !== null);

  if (winners.length !== 2) {
    return null;
  }

  return {
    id: 1,
    player1: winners[0].player,
    player2: winners[1].player,
    winner: null,
    score: null,
    frameScores: [],
    sourceMatch1: winners[0].matchId,
    sourceMatch2: winners[1].matchId
  };
};

/**
 * Check if all matches in a round are complete (have winners)
 */
export const isRoundComplete = (matches) => {
  if (!matches || matches.length === 0) return false;
  return matches.every(match => match.winner !== null);
};

/**
 * Get potential winner from a match (shows both players if no winner yet)
 */
const getPotentialWinner = (match) => {
  if (match.winner !== null) {
    return match.winner === 1 ? match.player1 : match.player2;
  }
  return null;
};

/**
 * Generate potential quarter-final matches showing "if X wins" scenarios
 */
export const generatePotentialQuarterFinals = (groupOf8Matches) => {
  const matches = [];
  
  // Get all match results (winners or potential players)
  const allMatches = groupOf8Matches.map(match => ({
    matchId: match.id,
    winner: getMatchWinner(match),
    player1: match.player1,
    player2: match.player2,
    sourceMatch1: match.sourceMatch1,
    sourceMatch2: match.sourceMatch2,
    // Check if this match involves a protected team
    isProtected: match.sourceMatch1 ? (isProtectedMatch(match.sourceMatch1) || isProtectedMatch(match.sourceMatch2)) : false,
    isComplete: match.winner !== null
  }));

  // Separate protected and non-protected matches
  const protectedMatches = allMatches.filter(m => m.isProtected);
  const nonProtectedMatches = allMatches.filter(m => !m.isProtected);

  // For potential matches, use consistent pairing (sorted by match ID)
  // This ensures the bracket structure is predictable
  const sortedNonProtected = [...nonProtectedMatches].sort((a, b) => a.matchId - b.matchId);

  let nonProtectedIndex = 0;
  let matchId = 1;

  // Sort protected matches by ID for consistency
  const sortedProtected = [...protectedMatches].sort((a, b) => a.matchId - b.matchId);

  // Pair each protected match with a non-protected match
  sortedProtected.forEach((protectedMatch) => {
    if (nonProtectedIndex < sortedNonProtected.length) {
      const opponentMatch = sortedNonProtected[nonProtectedIndex];
      nonProtectedIndex++;

      // Determine players based on who won or potential winners
      const player1 = protectedMatch.winner && protectedMatch.player1 && protectedMatch.player2
        ? (protectedMatch.winner === 1 ? protectedMatch.player1 : protectedMatch.player2)
        : { name: `Winner of G8 ${protectedMatch.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };
      
      const player2 = opponentMatch.winner && opponentMatch.player1 && opponentMatch.player2
        ? (opponentMatch.winner === 1 ? opponentMatch.player1 : opponentMatch.player2)
        : { name: `Winner of G8 ${opponentMatch.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };

      matches.push({
        id: matchId++,
        player1,
        player2,
        winner: null,
        score: null,
        sourceMatch1: protectedMatch.matchId,
        sourceMatch2: opponentMatch.matchId,
        isPotential: !protectedMatch.isComplete || !opponentMatch.isComplete,
        isProtectedMatch: true
      });
    }
  });

  // Pair remaining non-protected matches
  while (nonProtectedIndex < sortedNonProtected.length) {
    const match1 = sortedNonProtected[nonProtectedIndex];
    const match2 = sortedNonProtected[nonProtectedIndex + 1];
    
    if (match1 && match2) {
      const player1 = match1.winner && match1.player1 && match1.player2
        ? (match1.winner === 1 ? match1.player1 : match1.player2)
        : { name: `Winner of G8 ${match1.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };
      
      const player2 = match2.winner && match2.player1 && match2.player2
        ? (match2.winner === 1 ? match2.player1 : match2.player2)
        : { name: `Winner of G8 ${match2.matchId}`, image: null, note: 'To be determined', isPlaceholder: true };

      matches.push({
        id: matchId++,
        player1,
        player2,
        winner: null,
        score: null,
        sourceMatch1: match1.matchId,
        sourceMatch2: match2.matchId,
        isPotential: !match1.isComplete || !match2.isComplete,
        isProtectedMatch: false
      });
      nonProtectedIndex += 2;
    } else {
      break;
    }
  }

  return matches;
};

/**
 * Generate potential semi-final matches from quarter-final matches
 * 4 players, 2 matches
 */
export const generatePotentialSemiFinals = (quarterFinalMatches) => {
  if (!quarterFinalMatches || quarterFinalMatches.length !== 4) return [];

  // Create a map of matchId to match for easy lookup
  const matchesMap = new Map();
  quarterFinalMatches.forEach(match => {
    matchesMap.set(match.id, match);
  });

  // SF pairings: 1v3, 2v4
  const pairingMap = [
    [1, 3],
    [2, 4]
  ];

  return pairingMap.map(([ matchId1, matchId2 ], i) => {
    const match1 = matchesMap.get(matchId1);
    const match2 = matchesMap.get(matchId2);

    const player1 = getMatchWinner(match1)
      ? getMatchWinner(match1)
      : { name: `Winner of QF ${matchId1}`, image: null, note: 'To be determined', isPlaceholder: true };

    const player2 = getMatchWinner(match2)
      ? getMatchWinner(match2)
      : { name: `Winner of QF ${matchId2}`, image: null, note: 'To be determined', isPlaceholder: true };

    return {
      id: i + 1,
      player1,
      player2,
      winner: null,
      score: null,
      frameScores: [],
      sourceMatch1: matchId1,
      sourceMatch2: matchId2,
      isPotential: !match1?.winner || !match2?.winner
    };
  });
};

/**
 * Generate potential final match from semi-final matches
 */
export const generatePotentialFinal = (semiFinalMatches) => {
  if (!semiFinalMatches || semiFinalMatches.length !== 2) return null;

  const match1 = semiFinalMatches[0];
  const match2 = semiFinalMatches[1];
  
  if (!match1 || !match2) return null;

  // Get actual winner if available, otherwise use placeholder
  const player1 = getMatchWinner(match1)
    ? getMatchWinner(match1)
    : { name: `Winner of SF ${match1.id}`, image: null, note: 'To be determined', isPlaceholder: true };
  
  const player2 = getMatchWinner(match2)
    ? getMatchWinner(match2)
    : { name: `Winner of SF ${match2.id}`, image: null, note: 'To be determined', isPlaceholder: true };

  return {
    id: 1,
    player1,
    player2,
    winner: null,
    score: null,
    frameScores: [],
    sourceMatch1: match1.id,
    sourceMatch2: match2.id,
    isPotential: !match1.winner || !match2.winner
  };
};

/**
 * Update tournament bracket based on completed matches
 * Now shows potential matchups even when matches aren't complete
 */
export const updateTournamentBracket = (tournamentData) => {
  const updated = { ...tournamentData };

  // Generate Group of 8 from Round of 16
  if (updated.roundOf16 && updated.roundOf16.length > 0) {
    if (isRoundComplete(updated.roundOf16)) {
      updated.groupOf8 = generateGroupOf8(updated.roundOf16);
    } else {
      updated.groupOf8 = generatePotentialGroupOf8(updated.roundOf16);
    }
  }

  // Generate Quarter Finals from Group of 8 (8 players, 4 matches)
  // Always generate Quarter Finals, showing actual winners when available
  if (updated.groupOf8 && updated.groupOf8.length > 0) {
    // Check if we have at least some winners to show
    const hasWinners = updated.groupOf8.some(m => m.winner !== null);
    if (hasWinners) {
      // Try to generate actual matches if we have enough winners
      const actualMatches = generateQuarterFinalsFromGroupOf8(updated.groupOf8);
      if (actualMatches.length > 0) {
        updated.quarterFinals = actualMatches;
      } else {
        // Fall back to potential matches if not all winners are available
        updated.quarterFinals = generatePotentialQuarterFinalsFromGroupOf8(updated.groupOf8);
      }
    } else {
      // Show potential matchups if no winners yet
      updated.quarterFinals = generatePotentialQuarterFinalsFromGroupOf8(updated.groupOf8);
    }
  }

  // Generate Semi Finals from Quarter Finals (4 players, 2 matches)
  // Always generate Semi Finals, showing actual winners when available
  if (updated.quarterFinals && updated.quarterFinals.length === 4) {
    const hasWinners = updated.quarterFinals.some(m => m.winner !== null);
    if (hasWinners) {
      // Try to generate actual matches if we have enough winners
      const actualMatches = generateSemiFinals(updated.quarterFinals);
      if (actualMatches.length > 0) {
        updated.semiFinals = actualMatches;
      } else {
        // Fall back to potential matches if not all winners are available
        updated.semiFinals = generatePotentialSemiFinals(updated.quarterFinals);
      }
    } else {
      // Show potential matchups if no winners yet
      updated.semiFinals = generatePotentialSemiFinals(updated.quarterFinals);
    }
  }

  // Generate Final from Semi Finals (2 players, 1 match)
  // Always generate Final, showing actual winners when available
  if (updated.semiFinals && updated.semiFinals.length === 2) {
    const hasWinners = updated.semiFinals.some(m => m.winner !== null);
    if (hasWinners) {
      // Try to generate actual match if we have both winners
      const actualMatch = generateFinal(updated.semiFinals);
      if (actualMatch) {
        updated.final = actualMatch;
      } else {
        // Fall back to potential match if not all winners are available
        updated.final = generatePotentialFinal(updated.semiFinals);
      }
    } else {
      // Show potential matchup if no winners yet
      updated.final = generatePotentialFinal(updated.semiFinals);
    }
  }

  return updated;
};
